import { useEffect, useRef, useState } from "react";

import { HotkeysProvider } from "@tanstack/react-hotkeys";
import { Outlet, createFileRoute, useParams } from "@tanstack/react-router";
import { Brain, X } from "lucide-react";

import { LazyMotionProvider } from "@/components/common/LazyMotionProvider";
import { CotAccordion } from "@/components/chain-of-thought/CotAccordion";
import { ConversationList } from "@/components/conversations/ConversationList";
import { AppFooter } from "@/components/common/AppFooter";
import { AppGlobalHotkeys } from "@/components/common/AppGlobalHotkeys";
import { CguAcceptanceGate } from "@/components/common/CguAcceptanceGate";
import { HotkeysHelpDialog } from "@/components/common/HotkeysHelpDialog";
import { ServiceAnnouncementBanner } from "@/components/common/ServiceAnnouncementBanner";
import { Button } from "@/components/ui/button";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useChainOfThought } from "@/contexts/ChainOfThoughtContext";
import { ChainOfThoughtProvider } from "@/contexts/ChainOfThoughtProvider";
import {
  useChatComposerTextareaRef,
  useChatContext,
} from "@/contexts/ChatContext";
import { ChatProvider } from "@/contexts/ChatProvider";
import { ScrollEngineProvider } from "@/contexts/ScrollEngineProvider";
import {
  useConversationMutations,
  useDefaultTagIds,
  useDocumentTypes,
  useUserPreferences,
  useUserTags,
} from "@/tanstack-db/dbHooks";
import { useEffectiveShowChainOfThought } from "@/hooks/useEffectiveShowChainOfThought";
import { shouldPersistCloseChainOfThoughtOnNavigation } from "@/lib/ui-helper/cot-panel-visibility";
import { resolveScrollConversationKey } from "@/lib/scroll";

const TANSTACK_HOTKEYS_PROVIDER_DEFAULTS = {
  hotkey: {
    conflictBehavior: "replace" as const,
    preventDefault: true,
    stopPropagation: true,
  },
};

/** Fallback until SortFilterBar intrinsic width is measured. */
const SIDEBAR_PANEL_MIN_SIZE_FALLBACK_PX = 200;

function AppShellContent() {
  const params = useParams({ strict: false });
  const selectedConversation = params?.cId ?? undefined;
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [hotkeysHelpOpen, setHotkeysHelpOpen] = useState(false);
  const [sidebarMinSizePx, setSidebarMinSizePx] = useState(
    SIDEBAR_PANEL_MIN_SIZE_FALLBACK_PX
  );

  const defaultTagIds = useDefaultTagIds();
  const { toggleTheme } = useUserPreferences();
  const documentTypes = useDocumentTypes();
  const {
    userTags: customTags,
    createTag: createTagDB,
    deleteTag: deleteTagDB,
    updateTagColor: updateTagColorDB,
  } = useUserTags();
  const {
    deleteConversation: deleteConversationDB,
    updateConversationTags: updateConversationTagsDB,
  } = useConversationMutations();
  const {
    allChainOfThoughts,
    chainOfThoughtSteps,
    chainOfThoughtContent,
    isChainOfThoughtActive,
    currentStreamingCoTId,
    setShowChainOfThought,
  } = useChainOfThought();
  const effectiveShowChainOfThought = useEffectiveShowChainOfThought();
  const { messages, dispatch: chatDispatch } = useChatContext();
  const composerTextareaRef = useChatComposerTextareaRef();
  const prevConversationRef = useRef(selectedConversation);
  const setShowChainOfThoughtRef = useRef(setShowChainOfThought);
  setShowChainOfThoughtRef.current = setShowChainOfThought;

  useEffect(() => {
    const previousConversation = prevConversationRef.current;
    prevConversationRef.current = selectedConversation;

    if (
      shouldPersistCloseChainOfThoughtOnNavigation(
        previousConversation,
        selectedConversation
      )
    ) {
      setShowChainOfThoughtRef.current(false);
    }
  }, [selectedConversation]);

  const handleTagsUpdate = async (
    id: string | number,
    tags: string[],
    type: "documentTypes" | "customTags"
  ) => {
    try {
      await updateConversationTagsDB(String(id), tags, type);
    } catch (error) {
      console.error("Error updating conversation tags:", error);
    }
  };

  const handleNewConversation = () => {
    chatDispatch({ type: "NAVIGATE_CONVERSATION", conversationId: undefined });
    requestAnimationFrame(() => composerTextareaRef.current?.focus());
  };

  const handleSelectConversation = (id: number | string) => {
    chatDispatch({
      type: "NAVIGATE_CONVERSATION",
      conversationId: String(id),
    });
  };

  const handleDeleteConversation = async (id: string | number) => {
    try {
      const idStr = String(id);
      deleteConversationDB(idStr);
      if (selectedConversation === idStr) {
        chatDispatch({
          type: "NAVIGATE_CONVERSATION",
          conversationId: undefined,
        });
      }
    } catch (error) {
      console.error("Error deleting conversation:", error);
    }
  };

  const handleAddCustomTag = async (tag: Tag) => {
    try {
      createTagDB(tag);
    } catch (error) {
      console.error("Error creating tag:", error);
    }
  };

  const handleDeleteCustomTag = async (tagId: string) => {
    try {
      deleteTagDB(tagId);
    } catch (error) {
      console.error("Error deleting tag:", error);
    }
  };

  const handleUpdateCustomTagColor = (tagId: string, color: string) => {
    try {
      updateTagColorDB(tagId, color);
    } catch (error) {
      console.error("Error updating tag color:", error);
    }
  };

  return (
    <>
      <AppGlobalHotkeys
        searchInputRef={searchInputRef}
        onNewConversation={handleNewConversation}
        onToggleTheme={toggleTheme}
        onToggleHotkeys={() => setHotkeysHelpOpen((prev) => !prev)}
        onToggleChainOfThought={
          selectedConversation != null
            ? () => setShowChainOfThought((prev) => !prev)
            : undefined
        }
      />
      <HotkeysHelpDialog
        open={hotkeysHelpOpen}
        onOpenChange={setHotkeysHelpOpen}
      />
      <CguAcceptanceGate>
        <div className="flex h-screen min-h-0 flex-col bg-background">
          <ServiceAnnouncementBanner />
          <ResizablePanelGroup
            orientation="horizontal"
            className="min-h-0 flex-1">
            <ResizablePanel
              defaultSize={250}
              minSize={sidebarMinSizePx}
              className="overflow-hidden">
              <ConversationList
                selectedConversation={selectedConversation}
                documentTypes={documentTypes as Tag[]}
                customTags={customTags}
                defaultTagIds={defaultTagIds}
                onAddCustomTag={handleAddCustomTag}
                onDeleteCustomTag={handleDeleteCustomTag}
                onUpdateCustomTagColor={handleUpdateCustomTagColor}
                onSelectConversation={handleSelectConversation}
                onNewConversation={handleNewConversation}
                searchInputRef={searchInputRef}
                onExportTagConversations={() => {}}
                onShareTagConversations={() => {}}
                onTransferTagConversations={() => {}}
                onDeleteConversation={handleDeleteConversation}
                onTagsUpdate={handleTagsUpdate}
                onShowKeyboardShortcuts={() => setHotkeysHelpOpen(true)}
                onMinContentWidthChange={setSidebarMinSizePx}
              />
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel
              defaultSize={effectiveShowChainOfThought ? 550 : 800}
              minSize={400}>
              <div className="h-full">
                <Outlet />
              </div>
            </ResizablePanel>

            {effectiveShowChainOfThought ? (
              <>
                <ResizableHandle withHandle />
                <ResizablePanel
                  defaultSize={250}
                  minSize={150}
                  className="overflow-hidden">
                  <div
                    id="cot-side-panel"
                    className="h-full border-l border-border/15 flex flex-col animate-in slide-in-from-right duration-300"
                    style={{ animationFillMode: "backwards" }}>
                    <div className="min-h-20 items-center flex justify-between border-b border-border/15 px-5 py-4 shrink-0 bg-background/60 backdrop-blur-xl">
                      <div className="flex items-center gap-3 text-secondary">
                        <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-secondary/10">
                          <Brain className="h-4 w-4 stroke-secondary" />
                        </div>
                        <span className="text-sm font-semibold tracking-tight">
                          Étapes de recherche
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        onClick={() => setShowChainOfThought(false)}
                        aria-label="Fermer le panneau des étapes de recherche"
                        className="text-muted-foreground hover:text-foreground shrink-0">
                        <X className="size-3.5" />
                      </Button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 scrollbar-thin">
                      <CotAccordion
                        chainOfThoughts={allChainOfThoughts}
                        messages={messages}
                        currentStreamingId={currentStreamingCoTId}
                        streamingContent={chainOfThoughtContent}
                        streamingActions={chainOfThoughtSteps}
                        isActive={isChainOfThoughtActive}
                        conversationId={resolveScrollConversationKey(
                          selectedConversation
                        )}
                      />
                    </div>
                  </div>
                </ResizablePanel>
              </>
            ) : null}
          </ResizablePanelGroup>
          <AppFooter />
        </div>
      </CguAcceptanceGate>
    </>
  );
}

function AppLayoutRoute() {
  return (
    <LazyMotionProvider>
      <ChainOfThoughtProvider>
        <ScrollEngineProvider>
          <ChatProvider>
            <TooltipProvider>
              <HotkeysProvider
                defaultOptions={TANSTACK_HOTKEYS_PROVIDER_DEFAULTS}>
                <AppShellContent />
              </HotkeysProvider>
            </TooltipProvider>
          </ChatProvider>
        </ScrollEngineProvider>
      </ChainOfThoughtProvider>
    </LazyMotionProvider>
  );
}

export const Route = createFileRoute("/_app")({
  component: AppLayoutRoute,
});
